import { createAsyncThunk } from '@reduxjs/toolkit';

import { CommonState } from '../common/common.types';
import { GlobalState } from '../types';

import {
  setCampaigns,
  setCurrentCampaign,
  setNotifications,
  setStats,
} from './messages.slice';
import { MessagesState } from './messages.types';

import { getCampaignsAndStats } from '@/services/hsm-backend/get-campaigns-and-stats';
import { getFullCampaign } from '@/services/hsm-backend/get-full-campaign';
import { resendPendingMessages } from '@/services/hsm-backend/resend-pending-messages';
import { retryErroredMessages } from '@/services/hsm-backend/retry-errored-messages';
import { Campaign, Stats, StrapiHsmEvent } from '@/services/hsm-backend/types';
import { getNotificationEvents } from '@/services/hsm-backend/get-notification-events';

// TODO: tests para consulta de limites
interface FetchCampaignsAndStats {
  websiteId: CommonState['currentChannel'];
  dateRange: MessagesState['dateRange'];
}

interface FetchCurrentCampaign {
  campaignId: string;
}

type FetchNotificationEvents = FetchCampaignsAndStats;

/**
 * roadmap#1815 — guardia contra respuestas "stale".
 *
 * En cuentas con más de un canal de WhatsApp se disparan varios fetch en vuelo:
 * `currentChannel` cambia durante la inicialización (canal por defecto → canal
 * seleccionado) y el effect de `dateRange` vuelve a pedir. Sin este chequeo, la
 * respuesta que llega ÚLTIMA pisa el estado global de forma incondicional — y si
 * es la de una línea sin campañas, deja el listado vacío ("la campaña aparece un
 * segundo y desaparece"). Descartamos el resultado cuando el canal seleccionado
 * ya no coincide con el de esta request.
 */
const isStaleChannel = (
  getState: () => unknown,
  websiteId: CommonState['currentChannel']
): boolean => (getState() as GlobalState).common.currentChannel !== websiteId;

export const fetchCampaignsAction = createAsyncThunk(
  'messages/fetchCampaigns',
  async ({ websiteId, dateRange }: FetchCampaignsAndStats, thunkAPI) => {
    const { campaigns }: { campaigns: Campaign[] } = await getCampaignsAndStats(
      websiteId,
      dateRange
    );

    if (!isStaleChannel(thunkAPI.getState, websiteId)) {
      thunkAPI.dispatch(setCampaigns({ campaigns }));
    }

    return { campaigns };
  }
);

export const fetchStatsAction = createAsyncThunk(
  'messages/fetchStats',
  async ({ websiteId, dateRange }: FetchCampaignsAndStats, thunkAPI) => {
    const { stats }: { stats: Stats } = await getCampaignsAndStats(
      websiteId,
      dateRange
    );

    if (!isStaleChannel(thunkAPI.getState, websiteId)) {
      thunkAPI.dispatch(setStats({ stats }));
    }

    return { stats };
  }
);

export const fetchNotificationEventsAction = createAsyncThunk(
  'messages/fetchNotificationEvent',
  async ({ websiteId, dateRange }: FetchNotificationEvents, thunkAPI) => {
    const notifications: StrapiHsmEvent[] = await getNotificationEvents(
      websiteId,
      dateRange
    );

    if (!isStaleChannel(thunkAPI.getState, websiteId)) {
      thunkAPI.dispatch(setNotifications({ notifications }));
    }

    return notifications;
  }
);

export const fetchCurrentCampaignAction = createAsyncThunk(
  'messages/fetchCurrentCampaign',
  async ({ campaignId }: FetchCurrentCampaign, thunkAPI) => {
    const {
      common: { currentChannel },
    } = thunkAPI.getState() as GlobalState;

    const currentCampaign = await getFullCampaign(currentChannel, campaignId);

    thunkAPI.dispatch(setCurrentCampaign({ currentCampaign }));

    return currentCampaign;
  }
);

export const resendPendingMessagesAction = createAsyncThunk(
  'messages/resendPendingMessages',
  async (_, thunkAPI) => {
    const {
      messages: { currentCampaign },
      common: { currentChannel },
    } = thunkAPI.getState() as GlobalState;

    if (currentCampaign) {
      const result = await resendPendingMessages(
        currentChannel,
        currentCampaign['id']
      );

      return result;
    }

    return thunkAPI.rejectWithValue('currentCampaign is null');
  }
);

export const retryErroredMessagesAction = createAsyncThunk(
  'messages/retryErroredMessages',
  async (_, thunkAPI) => {
    const {
      messages: { currentCampaign },
      common: { currentChannel },
    } = thunkAPI.getState() as GlobalState;

    if (currentCampaign) {
      const result = await retryErroredMessages(
        currentChannel,
        currentCampaign['id']
      );

      return result;
    }

    return thunkAPI.rejectWithValue('currentCampaign is null');
  }
);
